import React, { useState, useEffect } from 'react';
import './DataQualityReview.css';
import '../styles/ResizableTable.css';
import { useUser } from '../contexts/UserContext';
import axios from 'axios';
import { api } from '../config';
import TextEditorModal from './TextEditorModal';
import { getDivisions } from '../utils/departments';
import { initResizableTables } from '../utils/resizableTables';
import Spinner from './Spinner';

interface Initiative {
  id: number;
  use_case_name: string;
  submitted_by: string;
  submitted_date: string;
  proposed_solution_owner: string;
  solution_sponsor: string;
  approved_by?: string;
  division?: string;
}

interface DataQualityReviewData {
  id?: number;
  initiative_id: number;
  reviewed_by: string;
  technical_stack_frontend: string;
  technical_stack_ai: string;
  structured_data_source: string;
  unstructured_data_sources: string;
  user_base: string;
  evaluated_existing_solutions: string;
  why_not_evaluated?: string;
  security_considerations: string;
  security_classification: string;
  third_party_components: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewer_comment: string;
}

const DataQualityReview: React.FC = () => {
  const { user, isReviewer, isDQReviewer, dqDivision } = useUser();
  const canAccess = isReviewer || isDQReviewer;
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [submittedReviews, setSubmittedReviews] = useState<any[]>([]);
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [reviewTab, setReviewTab] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [activeDQDivision, setActiveDQDivision] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<DataQualityReviewData>({
    initiative_id: 0,
    reviewed_by: user?.email || '',
    technical_stack_frontend: '',
    technical_stack_ai: '',
    structured_data_source: '',
    unstructured_data_sources: '',
    user_base: '',
    evaluated_existing_solutions: '',
    why_not_evaluated: '',
    security_considerations: '',
    security_classification: '',
    third_party_components: '',
    status: 'pending',
    reviewer_comment: '',
  });

  // Text editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingField, setEditingField] = useState<keyof DataQualityReviewData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [divisionSaving, setDivisionSaving] = useState<number | null>(null);

  // Workflow modal state
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [delegationFlow, setDelegationFlow] = useState<any>(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);

  // Fetch approved initiatives and submitted reviews - only if reviewer
  useEffect(() => {
    if (!canAccess) {
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [initiativesRes, submittedRes] = await Promise.all([
          axios.get(api('/api/data-quality-reviews/approved-initiatives')),
          axios.get(api('/api/data-quality-reviews/submitted/list'))
        ]);
        setInitiatives(initiativesRes.data);
        setSubmittedReviews(submittedRes.data);
      } catch (err: any) {
        setError('Failed to load reviews data');
        console.error('Error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.email) {
      fetchData();
    }
  }, [user?.email, canAccess]);

  // Initialize resizable tables when reviews load
  useEffect(() => {
    if (!isLoading && (initiatives.length > 0 || submittedReviews.length > 0)) {
      const timers = [
        setTimeout(() => initResizableTables(), 50),
        setTimeout(() => initResizableTables(), 200),
      ];
      return () => timers.forEach(t => clearTimeout(t));
    }
  }, [isLoading, initiatives.length, submittedReviews.length]);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editorOpen) {
          setEditorOpen(false);
          setEditingField(null);
        } else if (showWorkflowModal) {
          setShowWorkflowModal(false);
        } else if (selectedInitiative) {
          setSelectedInitiative(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [selectedInitiative, editorOpen, showWorkflowModal]);

  // Load existing review when initiative is selected
  useEffect(() => {
    if (selectedInitiative) {
      const loadReview = async () => {
        try {
          const response = await axios.get(
            api(`/api/data-quality-reviews/${selectedInitiative.id}`)
          );
          if (response.data) {
            setFormData(response.data);
          } else {
            // New review
            setFormData(prev => ({
              ...prev,
              initiative_id: selectedInitiative.id,
              reviewed_by: user?.email || '',
            }));
          }
        } catch (err: any) {
          console.error('Error loading review:', err);
          // Initialize form for new review
          setFormData(prev => ({
            ...prev,
            initiative_id: selectedInitiative.id,
            reviewed_by: user?.email || '',
          }));
        }
      };
      loadReview();
    }
  }, [selectedInitiative, user?.email]);

  // Check if reviewer
  if (!canAccess) {
    return (
      <div className="data-quality-review">
        <div style={{ padding: '40px', textAlign: 'center', color: '#d32f2f' }}>
          <p style={{ fontSize: '18px', fontWeight: '600' }}>Access Denied</p>
          <p>This feature is only accessible to reviewers and DQ reviewers.</p>
        </div>
      </div>
    );
  }

  const getFieldLabel = (field: keyof DataQualityReviewData): string => {
    const labels: Record<string, string> = {
      technical_stack_frontend: 'Technical Stack (Frontend)',
      technical_stack_ai: 'Technical Stack (AI)',
      structured_data_source: 'Structured Data Sources',
      unstructured_data_sources: 'Unstructured Data Sources',
      user_base: 'User Base',
      evaluated_existing_solutions: 'Evaluated Existing Solutions',
      security_considerations: 'Security Considerations',
      security_classification: 'Security Classification',
      third_party_components: '3rd Party/Open Source Components',
      reviewer_comment: 'Reviewer Comment',
    };
    return labels[field] || field;
  };

  const saveDivision = async (initiativeId: number, division: string) => {
    setDivisionSaving(initiativeId);
    try {
      await axios.patch(api(`/api/initiatives/${initiativeId}/division`), { division });
      setInitiatives(prev => prev.map(i => i.id === initiativeId ? { ...i, division } : i));
    } catch (err) {
      console.error('Failed to save division', err);
    } finally {
      setDivisionSaving(null);
    }
  };

  const openEditor = (field: keyof DataQualityReviewData) => {
    setEditingField(field);
    setEditorOpen(true);
  };

  const saveEditorText = (text: string) => {
    if (editingField) {
      setFormData(prev => ({
        ...prev,
        [editingField]: text,
      }));
    }
    setEditorOpen(false);
    setEditingField(null);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      status: e.target.value as 'pending' | 'approved' | 'rejected',
    }));
  };

  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    
    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths}mo ago`;
  };

  const formatDateWithTime = (dateString: string) => {
    const date = new Date(dateString);
    
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const fetchDelegationFlow = async (initiativeId: number) => {
    setWorkflowLoading(true);
    setShowWorkflowModal(true);
    try {
      const response = await axios.get(
        api(`/api/reviews/flow/${initiativeId}`)
      );
      console.log(`[WORKFLOW] Fetched delegation flow for initiative ${initiativeId}:`, response.data);
      console.log(`[WORKFLOW] Total steps in flow: ${response.data.flow?.length || 0}`);
      response.data.flow?.forEach((step: any, index: number) => {
        console.log(`[WORKFLOW] Step ${index + 1}: ${step.from_name} → ${step.to_name} (order: ${step.step})`);
      });
      setDelegationFlow(response.data);
    } catch (error) {
      console.error('Error fetching delegation flow:', error);
      setError('Failed to load workflow details');
      setShowWorkflowModal(false);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      await axios.post(
        api(`/api/data-quality-reviews/${formData.initiative_id}`),
        formData
      );
      setSuccessMessage('Data quality review saved as draft');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError('Failed to save review');
      console.error('Error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      // First save the form data
      await axios.post(
        api(`/api/data-quality-reviews/${formData.initiative_id}`),
        formData
      );
      // Then submit
      await axios.post(
        api(`/api/data-quality-reviews/${formData.initiative_id}/submit`)
      );
      setSuccessMessage('Data quality review submitted and notifications sent');
      setTimeout(() => {
        setSuccessMessage(null);
        setSelectedInitiative(null);
        setFormData({
          initiative_id: 0,
          reviewed_by: user?.email || '',
          technical_stack_frontend: '',
          technical_stack_ai: '',
          structured_data_source: '',
          unstructured_data_sources: '',
          user_base: '',
          evaluated_existing_solutions: '',
          security_considerations: '',
          security_classification: '',
          third_party_components: '',
          status: 'pending',
          reviewer_comment: '',
        });
        // Reload initiatives and reviews
        const fetchData = async () => {
          try {
            const [initiativesRes, submittedRes] = await Promise.all([
              axios.get(api('/api/data-quality-reviews/approved-initiatives')),
              axios.get(api('/api/data-quality-reviews/submitted/list'))
            ]);
            setInitiatives(initiativesRes.data);
            setSubmittedReviews(submittedRes.data);
          } catch (err) {
            console.error('Error reloading data:', err);
          }
        };
        fetchData();
      }, 2000);
    } catch (err: any) {
      setError('Failed to submit review');
      console.error('Error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="data-quality-review">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #e8e8e8', borderTop: '4px solid #ee0000', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
          <p style={{ fontSize: '14px', color: '#666', fontWeight: 500 }}>Fetching approved initiatives from database...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="data-quality-review">

      {error && (
        <div className="error-message" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="success-message" style={{ marginBottom: '20px' }}>
          {successMessage}
        </div>
      )}

      {!selectedInitiative ? (
        <div className="initiatives-section">
          {/* Status Tabs */}
          <div className="status-tabs">
            <button
              className={`status-tab ${reviewTab === 'pending' ? 'status-tab--active' : ''}`}
              onClick={() => { setReviewTab('pending'); setActiveDQDivision(getDivisions()[0] || null); }}
            >
              Pending ({initiatives.length})
            </button>
            <button
              className={`status-tab ${reviewTab === 'approved' ? 'status-tab--active' : ''}`}
              onClick={() => { setReviewTab('approved'); setActiveDQDivision(getDivisions()[0] || null); }}
            >
              Approved ({submittedReviews.filter(r => r.review_status === 'approved').length})
            </button>
            <button
              className={`status-tab ${reviewTab === 'rejected' ? 'status-tab--active' : ''}`}
              onClick={() => { setReviewTab('rejected'); setActiveDQDivision(getDivisions()[0] || null); }}
            >
              Rejected ({submittedReviews.filter(r => r.review_status === 'rejected').length})
            </button>
          </div>

          {/* Division Subtabs */}
          <div className="division-subtabs">
            {getDivisions().map(div => {
              const dept = div;
              let countForDept = 0;
              if (reviewTab === 'pending') {
                countForDept = initiatives.filter(i => i.division === dept).length;
              } else if (reviewTab === 'approved') {
                countForDept = submittedReviews.filter(r => r.review_status === 'approved' && r.division === dept).length;
              } else if (reviewTab === 'rejected') {
                countForDept = submittedReviews.filter(r => r.review_status === 'rejected' && r.division === dept).length;
              }
              return (
                <button
                  key={dept}
                  className={`division-subtab ${activeDQDivision === dept ? 'division-subtab--active' : ''}`}
                  onClick={() => setActiveDQDivision(dept)}
                >
                  {dept} <span className="division-subtab__count">
                    {isLoading ? '...' : countForDept}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Pending Tab */}
          {reviewTab === 'pending' && (
            <>
              <p style={{ color: '#666', marginBottom: '20px', marginTop: '16px' }}>
                Select an approved initiative to begin data quality assessment
              </p>

              {initiatives.filter(i => !activeDQDivision || i.division === activeDQDivision).length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                  No approved initiatives available for review
                </div>
              ) : (
                <div className="initiatives-table-wrapper">
                  <table id="dq-pending-table" className="initiatives-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Initiative Name</th>
                        <th>Division</th>
                        <th>Submitted By</th>
                        <th>Owner</th>
                        <th>Initiative Approved By</th>
                        <th>Submitted Date</th>
                        <th>DQ Review Approved By</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {initiatives.filter(i => !activeDQDivision || i.division === activeDQDivision).map((initiative) => (
                        <tr key={initiative.id} className="initiative-row">
                          <td className="id-cell">#{initiative.id}</td>
                          <td className="name-cell">{initiative.use_case_name}</td>
                          <td style={{ minWidth: '130px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <select
                                value={initiative.division || ''}
                                onChange={(e) => saveDivision(initiative.id, e.target.value)}
                                disabled={divisionSaving === initiative.id}
                                style={{ padding: '4px 6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', flex: 1 }}
                              >
                                <option value="">— Not set —</option>
                                {getDivisions().map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                              {divisionSaving === initiative.id && <span style={{ fontSize: '12px', color: '#888' }}>⏳</span>}
                            </div>
                          </td>
                          <td>{initiative.submitted_by}</td>
                          <td>{initiative.proposed_solution_owner}</td>
                          <td>{initiative.approved_by || 'N/A'}</td>
                          <td>{new Date(initiative.submitted_date).toLocaleDateString()}</td>
                          <td>N/A</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                              <button
                                className="btn-icon-review"
                                onClick={() => setSelectedInitiative(initiative)}
                                title="Review initiative"
                              >
                                <img src="/reviewinitiative.png" alt="Review initiative" />
                              </button>
                              <button
                                className="btn-icon-workflow"
                                onClick={() => fetchDelegationFlow(initiative.id)}
                                title="View workflow"
                                disabled={workflowLoading}
                              >
                                <img src="/viewworkflow.png" alt="View workflow" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Approved Tab */}
          {reviewTab === 'approved' && (
            <>
              <p style={{ color: '#666', marginBottom: '20px', marginTop: '16px' }}>
                Reviews that have been approved
              </p>

              {submittedReviews.filter(r => r.review_status === 'approved' && (!activeDQDivision || r.division === activeDQDivision)).length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                  No approved reviews
                </div>
              ) : (
                <div className="initiatives-table-wrapper">
                  <table id="dq-completed-table" className="initiatives-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Initiative Name</th>
                        <th>Division</th>
                        <th>Submitted By</th>
                        <th>DQ Reviewed By</th>
                        <th>Review Date</th>
                        <th>DQ Review Approved By</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submittedReviews.filter(r => r.review_status === 'approved' && (!activeDQDivision || r.division === activeDQDivision)).map((review) => (
                        <tr key={review.id} className="initiative-row">
                          <td className="id-cell">#{review.initiative_id}</td>
                          <td className="name-cell">{review.use_case_name}</td>
                          <td>{review.division || '—'}</td>
                          <td>{review.submitted_by}</td>
                          <td>{review.reviewed_by}</td>
                          <td>{review.review_submitted_date ? new Date(review.review_submitted_date).toLocaleDateString() : 'N/A'}</td>
                          <td>{review.dq_approved_by || 'N/A'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                              <button
                                className="btn-icon-review"
                                onClick={() => fetchDelegationFlow(review.initiative_id)}
                                title="View workflow"
                                disabled={workflowLoading}
                              >
                                📊
                              </button>
                              <button
                                className="btn-icon-review"
                                onClick={() => setSelectedInitiative({
                                  id: review.initiative_id,
                                  use_case_name: review.use_case_name,
                                  submitted_by: review.submitted_by,
                                  submitted_date: review.submitted_date,
                                  proposed_solution_owner: review.proposed_solution_owner,
                                  solution_sponsor: review.solution_sponsor,
                                  approved_by: review.approved_by,
                                })}
                                title="View review"
                              >
                                👁️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Rejected Tab */}
          {reviewTab === 'rejected' && (
            <>
              <p style={{ color: '#666', marginBottom: '20px', marginTop: '16px' }}>
                Reviews that have been rejected
              </p>

              {submittedReviews.filter(r => r.review_status === 'rejected' && (!activeDQDivision || r.division === activeDQDivision)).length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                  No rejected reviews
                </div>
              ) : (
                <div className="initiatives-table-wrapper">
                  <table id="dq-rejected-table" className="initiatives-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Initiative Name</th>
                        <th>Division</th>
                        <th>Submitted By</th>
                        <th>DQ Reviewed By</th>
                        <th>Review Date</th>
                        <th>DQ Review Approved By</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submittedReviews.filter(r => r.review_status === 'rejected' && (!activeDQDivision || r.division === activeDQDivision)).map((review) => (
                        <tr key={review.id} className="initiative-row">
                          <td className="id-cell">#{review.initiative_id}</td>
                          <td className="name-cell">{review.use_case_name}</td>
                          <td>{review.division || '—'}</td>
                          <td>{review.submitted_by}</td>
                          <td>{review.reviewed_by}</td>
                          <td>{review.review_submitted_date ? new Date(review.review_submitted_date).toLocaleDateString() : 'N/A'}</td>
                          <td>{review.dq_approved_by || 'N/A'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                              <button
                                className="btn-icon-review"
                                onClick={() => fetchDelegationFlow(review.initiative_id)}
                                title="View workflow"
                                disabled={workflowLoading}
                              >
                                📊
                              </button>
                              <button
                                className="btn-icon-review"
                                onClick={() => setSelectedInitiative({
                                  id: review.initiative_id,
                                  use_case_name: review.use_case_name,
                                  submitted_by: review.submitted_by,
                                  submitted_date: review.submitted_date,
                                  proposed_solution_owner: review.proposed_solution_owner,
                                  solution_sponsor: review.solution_sponsor,
                                  approved_by: review.approved_by,
                                })}
                                title="View review"
                              >
                                👁️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="data-quality-review-form">
          <div className="form-container">
            <div className="form-header">
              <div>
                <h2>Data Quality Review</h2>
                <p className="initiative-id">ID: {selectedInitiative.id}</p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => fetchDelegationFlow(selectedInitiative.id)}
                  disabled={workflowLoading}
                  title="View the workflow status and delegation chain"
                >
                  📊 View Workflow
                </button>
                <button
                  className="btn-back"
                  onClick={() => setSelectedInitiative(null)}
                  disabled={isSaving}
                >
                  ← Back to Initiatives
                </button>
              </div>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {successMessage && (
              <div className="success-message">
                {successMessage}
              </div>
            )}

            {/* Initiative Details Section */}
            <div className="form-section">
              <h3 className="section-heading">Initiative Details</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Use Case Name</label>
                  <div className="read-only-value">{selectedInitiative.use_case_name}</div>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Submitted By</label>
                  <div className="read-only-value">{selectedInitiative.submitted_by}</div>
                </div>
                <div className="form-group">
                  <label>Solution Owner</label>
                  <div className="read-only-value">{selectedInitiative.proposed_solution_owner}</div>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Solution Sponsor</label>
                  <div className="read-only-value">{selectedInitiative.solution_sponsor}</div>
                </div>
                <div className="form-group">
                  <label>Submitted Date</label>
                  <div className="read-only-value">{new Date(selectedInitiative.submitted_date).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Approved By</label>
                  <div className="read-only-value">{selectedInitiative.approved_by || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Data Quality Assessment Fields Section */}
            <div className="form-section">
              <h3 className="section-heading">Data Quality Assessment</h3>
              
              {/* Row 1: Technical Stack (Frontend) + Technical Stack (AI) */}
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="technical_stack_frontend">
                    Technical Stack (Frontend)
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => openEditor('technical_stack_frontend')}
                      title="Open full editor"
                    >
                      (expand)
                    </button>
                  </label>
                  <textarea
                    id="technical_stack_frontend"
                    value={formData.technical_stack_frontend}
                    onChange={(e) => setFormData(prev => ({ ...prev, technical_stack_frontend: e.target.value }))}
                    placeholder="Describe the frontend technical stack"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="technical_stack_ai">
                    Technical Stack (AI)
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => openEditor('technical_stack_ai')}
                      title="Open full editor"
                    >
                      (expand)
                    </button>
                  </label>
                  <textarea
                    id="technical_stack_ai"
                    value={formData.technical_stack_ai}
                    onChange={(e) => setFormData(prev => ({ ...prev, technical_stack_ai: e.target.value }))}
                    placeholder="Describe the AI technical stack"
                  />
                </div>
              </div>

              {/* Row 2: Structured Data Sources + Unstructured Data Sources */}
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="structured_data_source">
                    Structured Data Sources
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => openEditor('structured_data_source')}
                      title="Open full editor"
                    >
                      (expand)
                    </button>
                  </label>
                  <textarea
                    id="structured_data_source"
                    value={formData.structured_data_source}
                    onChange={(e) => setFormData(prev => ({ ...prev, structured_data_source: e.target.value }))}
                    placeholder="Describe structured data sources"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="unstructured_data_sources">
                    Unstructured Data Sources
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => openEditor('unstructured_data_sources')}
                      title="Open full editor"
                    >
                      (expand)
                    </button>
                  </label>
                  <textarea
                    id="unstructured_data_sources"
                    value={formData.unstructured_data_sources}
                    onChange={(e) => setFormData(prev => ({ ...prev, unstructured_data_sources: e.target.value }))}
                    placeholder="Describe unstructured data sources"
                  />
                </div>
              </div>

              {/* Row 3: User Base + Evaluated Existing Solutions */}
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="user_base">
                    User Base
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => openEditor('user_base')}
                      title="Open full editor"
                    >
                      (expand)
                    </button>
                  </label>
                  <textarea
                    id="user_base"
                    value={formData.user_base}
                    onChange={(e) => setFormData(prev => ({ ...prev, user_base: e.target.value }))}
                    placeholder="Describe the user base"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="evaluated_existing_solutions">
                    Evaluated Existing Solutions
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => openEditor('evaluated_existing_solutions')}
                      title="Open full editor"
                    >
                      (expand)
                    </button>
                  </label>
                  <textarea
                    id="evaluated_existing_solutions"
                    value={formData.evaluated_existing_solutions}
                    onChange={(e) => setFormData(prev => ({ ...prev, evaluated_existing_solutions: e.target.value }))}
                    placeholder="Describe evaluated existing solutions"
                  />
                </div>
              </div>

              {/* Row 4: Security Considerations + Security Classification */}
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="security_considerations">
                    Security Considerations
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => openEditor('security_considerations')}
                      title="Open full editor"
                    >
                      (expand)
                    </button>
                  </label>
                  <textarea
                    id="security_considerations"
                    value={formData.security_considerations}
                    onChange={(e) => setFormData(prev => ({ ...prev, security_considerations: e.target.value }))}
                    placeholder="Describe security considerations"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="security_classification">
                    Security Classification
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => openEditor('security_classification')}
                      title="Open full editor"
                    >
                      (expand)
                    </button>
                  </label>
                  <textarea
                    id="security_classification"
                    value={formData.security_classification}
                    onChange={(e) => setFormData(prev => ({ ...prev, security_classification: e.target.value }))}
                    placeholder="Describe security classification"
                  />
                </div>
              </div>

              {/* Row 5: Third Party Components */}
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="third_party_components">
                    3rd Party/Open Source Components
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => openEditor('third_party_components')}
                      title="Open full editor"
                    >
                      (expand)
                    </button>
                  </label>
                  <textarea
                    id="third_party_components"
                    value={formData.third_party_components}
                    onChange={(e) => setFormData(prev => ({ ...prev, third_party_components: e.target.value }))}
                    placeholder="Describe third party and open source components"
                  />
                </div>
              </div>
            </div>

            {/* Reviewer Comment Section */}
            <div className="form-section">
              <h3 className="section-heading">Reviewer Comment</h3>
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="reviewer_comment">
                    Comments
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => openEditor('reviewer_comment')}
                      title="Open full editor"
                    >
                      (expand)
                    </button>
                  </label>
                  <textarea
                    id="reviewer_comment"
                    value={formData.reviewer_comment}
                    onChange={(e) => setFormData(prev => ({ ...prev, reviewer_comment: e.target.value }))}
                    placeholder="Add any reviewer comments"
                    style={{ minHeight: '150px' }}
                  />
                </div>
              </div>
            </div>

            {/* Review Status Section */}
            <div className="form-section">
              <h3 className="section-heading">Review Status</h3>
              <div className="status-radio-group">
                {(['pending', 'approved', 'rejected'] as const).map((val) => (
                  <label key={val} className={`status-radio-label status-radio-${val}${formData.status === val ? ' selected' : ''}`}>
                    <input
                      type="radio"
                      name="status"
                      value={val}
                      checked={formData.status === val}
                      onChange={() => setFormData(prev => ({ ...prev, status: val }))}
                      disabled={isSaving}
                    />
                    {val.charAt(0).toUpperCase() + val.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="button-group" style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
              {(() => {
                const initiativeDivision = selectedInitiative?.division || null;
                const canEdit = isReviewer || (isDQReviewer && (!dqDivision || dqDivision === initiativeDivision));
                return (
                  <>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setSelectedInitiative(null)}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveDraft}
                      disabled={isSaving || !canEdit}
                      title={!canEdit ? `You can only edit DQ reviews for division: ${dqDivision}` : ''}
                    >
                      {isSaving ? 'Saving...' : 'Save Draft'}
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={handleSubmit}
                      disabled={isSaving || formData.status === 'pending' || !canEdit}
                      title={!canEdit ? `You can only submit DQ reviews for division: ${dqDivision}` : ''}
                    >
                      {isSaving ? 'Submitting...' : 'Submit Review'}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Text Editor Modal */}
      {editorOpen && editingField && (
        <TextEditorModal
          isOpen={editorOpen}
          title={getFieldLabel(editingField)}
          value={String(formData[editingField] || '')}
          onSave={saveEditorText}
          onCancel={() => {
            setEditorOpen(false);
            setEditingField(null);
          }}
        />
      )}

      {/* Workflow Modal */}
      {showWorkflowModal && (
        <div className="modal-overlay" onClick={() => !workflowLoading && setShowWorkflowModal(false)}>
          <div className="modal-content workflow-modal" style={{ maxWidth: '1000px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 Workflow</h3>
              <button className="modal-close" onClick={() => !workflowLoading && setShowWorkflowModal(false)} disabled={workflowLoading}>×</button>
            </div>
            <div className="modal-body">
              {workflowLoading ? (
                <div className="workflow-loading">
                  <div className="spinner"></div>
                  <p>Loading delegation flow...</p>
                </div>
              ) : delegationFlow && (delegationFlow.flow?.length > 0 || delegationFlow.current_reviewer_email || delegationFlow.approval_status) ? (
                <div className="workflow-container">
                  {/* Show approval/rejection status if available */}
                  {delegationFlow.approval_status ? (
                    <>
                      {/* Show submission and approval timeline info as horizontal flow */}
                      <div style={{
                        marginTop: '15px',
                        display: 'flex',
                        gap: '15px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                        overflowX: 'auto'
                      }}>
                        <div style={{
                          padding: '16px 20px',
                          backgroundColor: '#e3f2fd',
                          borderLeft: '4px solid #2196f3',
                          borderRadius: '4px',
                          fontSize: '14px',
                          minWidth: '220px'
                        }}>
                          <div style={{ color: '#1565c0', fontWeight: '600', marginBottom: '6px', fontSize: '15px' }}>Request Submitted</div>
                          <div style={{ color: '#666', fontSize: '13px', marginBottom: '8px' }}>
                            {delegationFlow?.submitted_by_name || delegationFlow?.submitted_by || 'N/A'}
                          </div>
                          <div style={{ color: '#333', fontSize: '13px' }}>
                            {delegationFlow?.submitted_date ? formatDateWithTime(delegationFlow.submitted_date) : 'N/A'}
                          </div>
                        </div>
                        <div style={{ color: '#999', fontSize: '20px', fontWeight: '300' }}>→</div>
                        <div style={{
                          padding: '16px 20px',
                          backgroundColor: delegationFlow.approval_status === 'approved' ? '#e8f5e9' : '#ffebee',
                          borderLeft: `4px solid ${delegationFlow.approval_status === 'approved' ? '#4caf50' : '#f44336'}`,
                          borderRadius: '4px',
                          fontSize: '14px',
                          minWidth: '220px'
                        }}>
                          <div style={{ color: delegationFlow.approval_status === 'approved' ? '#2e7d32' : '#ee0000', fontWeight: '600', marginBottom: '6px', fontSize: '15px' }}>
                            {delegationFlow.approval_status === 'approved' ? 'Approved' : 'Rejected'}
                          </div>
                          <div style={{ color: '#666', fontSize: '13px', marginBottom: '8px' }}>
                            {delegationFlow.approved_by || 'N/A'}
                          </div>
                          <div style={{ color: '#333', fontSize: '13px' }}>
                            {delegationFlow.approval_date ? formatDateWithTime(delegationFlow.approval_date) : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : delegationFlow.flow && delegationFlow.flow.length > 0 ? (
                    <div className="horizontal-timeline">
                      {/* Show initial requestor */}
                      <div className="timeline-box" style={{ backgroundColor: '#fff3e0', borderColor: '#ff9800', width: '140px' }}>
                        <div className="box-email" style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Initiated By</div>
                        <div className="box-email" style={{ fontSize: '13px', marginBottom: '4px' }}>{delegationFlow?.submitted_by_name || delegationFlow?.submitted_by || 'N/A'}</div>
                        <div className="box-time">{delegationFlow?.submitted_date ? formatDateWithTime(delegationFlow.submitted_date) : 'N/A'}</div>
                      </div>
                      <div className="timeline-arrow">→</div>
                      {delegationFlow.flow.map((step: any, index: number) => (
                        <React.Fragment key={step.step}>
                          <div className="timeline-box" style={{ width: '140px' }}>
                            <div className="box-email" style={{ fontSize: '13px', marginBottom: '4px' }}>From: {step.from_name || step.from_email}</div>
                            <div className="box-time" style={{ fontSize: '12px', marginBottom: '6px' }}>↓</div>
                            <div className="box-email" style={{ fontSize: '13px', fontWeight: '600' }}>To: {step.to_name || step.to_email}</div>
                            <div className="box-time" style={{ fontSize: '12px', marginTop: '4px' }}>{formatRelativeTime(step.delegated_date)}</div>
                          </div>
                          {index < delegationFlow.flow.length - 1 && (
                            <div className="timeline-arrow">→</div>
                          )}
                        </React.Fragment>
                      ))}
                      {delegationFlow.current_reviewer_email && (
                        <>
                          <div className="timeline-arrow">→</div>
                          <div className="timeline-box current" style={{ backgroundColor: '#e3f2fd', width: '140px' }}>
                            <div className="box-email" style={{ fontSize: '13px', fontWeight: '600' }}>Current Reviewer</div>
                            <div className="box-email" style={{ fontSize: '13px', marginTop: '4px' }}>{delegationFlow.current_reviewer_name || delegationFlow.current_reviewer_email}</div>
                            <div className="box-time">now</div>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="horizontal-timeline">
                      <div className="timeline-box current" style={{ width: '140px' }}>
                        <div className="box-email">{delegationFlow.current_reviewer_email}</div>
                        <div className="box-time">Reviewing</div>
                      </div>
                    </div>
                  )}

                  <div className="workflow-summary">
                  </div>
                </div>
              ) : (
                <div className="no-workflow">
                  <p>No workflow history found</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowWorkflowModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataQualityReview;
